import axios from "axios";
import api from "lib/axios";
import { authStorage } from "services/authStorage";

export type UploadGroup = "avatars" | "images" | "documents" | "videos" | "audios" | "archives" | "misc";

export interface UploadResponse {
  key: string;
  url: string;
}

export interface PresignPayload {
  file_name: string;
  content_type: string;
  group?: UploadGroup;
}

export interface PresignResult {
  key: string;
  upload_url: string;
  readable_url?: string;
}


export const r2Service = {
  inferGroup(file: File | Blob): UploadGroup {
    const type = (file as any)?.type ? String((file as any).type) : "";
    if (type.startsWith("image/")) return "images";
    if (type.startsWith("video/")) return "videos";
    if (type.startsWith("audio/")) return "audios";
    return "documents";
  },

  async getPresignedUrl(payload: PresignPayload): Promise<PresignResult> {
    const token = authStorage.getTokenForPath();
    const presignUrl = `${process.env.REACT_APP_API_URL}/common/uploads/presign`;

    const response = await axios.post<{ status: string; result: { data: PresignResult } }>(
      presignUrl,
      payload,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    return response.data?.result?.data;
  },

  async uploadWithPresign(
    file: File | Blob,
    group: UploadGroup = "misc",
    onProgress?: (percent: number) => void
  ): Promise<UploadResponse> {
    const contentType =
      file instanceof File
        ? file.type || "application/octet-stream"
        : // Blob.type can be empty
          file.type || "application/octet-stream";

    const inferred = group === "misc" ? this.inferGroup(file) : group;
    const payload: PresignPayload = {
      file_name: file instanceof File ? file.name : "file",
      content_type: contentType,
      group: inferred,
    };

    const presign = await this.getPresignedUrl(payload);

    const totalBytes = file instanceof Blob ? file.size : 0;

    try {
      await axios.put(presign.upload_url, file, {
        headers: {
          "Content-Type": contentType,
        },
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
        onUploadProgress: (e) => {
          if (!onProgress) return;
          const total = e.total || totalBytes;
          if (total > 0) {
            onProgress(Math.min(100, Math.round((e.loaded / total) * 100)));
          }
        },
      });
    } catch (err: any) {
      const status = err?.response?.status;
      throw new Error(status ? `Upload failed: ${status}` : err?.message ?? "Upload failed");
    }

    const baseUrl = presign.readable_url || presign.upload_url.split("?")[0];
    return {
      key: presign.key,
      url: baseUrl,
    };
  },

  async upload(
    file: File | Blob,
    group: UploadGroup = "misc"
  ): Promise<UploadResponse> {
    const response = await api.post<{ status: string; result: { data: UploadResponse }; message: string }>(
      "/common/uploads/file",
      {
        file_name: file instanceof File ? file.name : "file",
        content_type: file.type,
        group,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (response.data.status !== "success") {
      throw new Error(response.data.message || "Upload failed");
    }

    return response.data?.result?.data;
  },
};
